import { t } from "i18next";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";

import { FLASHPAY_ROLES, hasAnyRole, hasRole } from "@/core/auth";
import { RowActionsMenu } from "@/core/components/shared/data-table";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useDeleteProductCode } from "@/features/ledger/api/use-delete-product-code";
import { ProductCodeFormDialog } from "@/features/ledger/components/product-codes/product-code-form-dialog";
import type { ProductCodeRow } from "@/features/ledger/components/product-codes/product-codes-columns";

/**
 * Menu d'actions de ligne des codes produits.
 * Édition (`PUT`) réservée à flashpay-superadmin / flashpay-admin ;
 * désactivation (`DELETE`) réservée à flashpay-superadmin (masquage).
 */
export function ProductCodeRowActions({
  productCode,
  navigate: _navigate,
}: {
  productCode: ProductCodeRow;
  navigate: NavigateFunction;
}) {
  const remove = useDeleteProductCode();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = hasAnyRole([
    FLASHPAY_ROLES.SUPERADMIN,
    FLASHPAY_ROLES.ADMIN,
  ]);
  const canDelete = hasRole(FLASHPAY_ROLES.SUPERADMIN);

  return (
    <>
      <RowActionsMenu
        actions={[
          ...(canEdit
            ? [
                {
                  id: "edit",
                  label: t("ledger.productCodes.actions.edit"),
                  icon: Pencil,
                  onClick: () => setEditOpen(true),
                },
              ]
            : []),
          ...(canDelete
            ? [
                {
                  id: "delete",
                  label: t("ledger.productCodes.actions.delete"),
                  icon: Trash2,
                  variant: "destructive" as const,
                  onClick: () => setDeleteOpen(true),
                },
              ]
            : []),
        ]}
      />

      {canEdit ? (
        <ProductCodeFormDialog
          productCode={productCode}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      ) : null}

      <MutationConfirmPreview
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        data={{
          id: productCode.id,
          code: productCode.code,
          label: productCode.label,
        }}
        title={t("common.mutationPreview.deleteTitle")}
        description={t("common.mutationPreview.deleteDescription")}
        confirmLabel={t("ledger.productCodes.actions.delete")}
        t={t}
        pending={remove.isPending}
        onConfirm={() =>
          remove.mutate(productCode.id, {
            onSuccess: () => {
              notifSilence(t("ledger.productCodes.feedback.deleted"));
              setDeleteOpen(false);
            },
            onError: (error) => notifSilenceError(error.message),
          })
        }
      />
    </>
  );
}
